import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface ErrorStateProps {
  message?: string
  onRetry: () => void
}

/** 조회 실패 — 기존에는 빈 화면만 보였다 */
export function BookmarkErrorState({ message, onRetry }: ErrorStateProps) {
  const { t } = useTranslation()
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-900 dark:bg-rose-950/30">
      <ExclamationCircleIcon className="mx-auto mb-3 h-10 w-10 text-rose-500" />
      <p className="text-sm font-medium text-rose-800 dark:text-rose-200">
        {t('bookmark.error.title')}
      </p>
      {message && (
        <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
          {message}
        </p>
      )}
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700"
      >
        {t('common.retry')}
      </button>
    </div>
  )
}

interface EmptyStateProps {
  /** 검색어. 있으면 검색 결과 없음으로 안내한다 */
  query?: string
  /** 검색어 말고 다른 조건(폴더·태그·보안·즐겨찾기)이 걸려 있는지 */
  hasOtherFilters: boolean
  isFavoritesView: boolean
  onCreate: () => void
  onClearQuery: () => void
  onSearchEverywhere: () => void
  onClearFilters: () => void
}

/**
 * 빈 목록.
 *
 * 왜 비었는지에 따라 할 수 있는 일이 다르다. 셋을 구분하지 않으면
 * "필터를 지우라"는 말을 링크가 하나도 없는 사람에게도 하게 된다.
 * - 검색 결과 없음 → 검색어를 지우거나 범위를 넓힌다
 * - 조건에 걸림   → 조건을 푼다
 * - 정말 비었음   → 링크를 만든다
 */
export function BookmarkEmptyState({
  query,
  hasOtherFilters,
  isFavoritesView,
  onCreate,
  onClearQuery,
  onSearchEverywhere,
  onClearFilters,
}: EmptyStateProps) {
  const { t } = useTranslation()

  if (query) {
    return (
      <div className="flex flex-col items-center gap-2.5 rounded-[18px] border border-gray-200 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-gray-800">
        <MagnifyingGlassIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
        <p className="text-base font-bold text-gray-900 dark:text-white">
          {t('bookmark.empty.queryTitle', { query })}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('bookmark.empty.queryHint')}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={onClearQuery}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {t('bookmark.empty.clearQuery')}
          </button>
          {hasOtherFilters && (
            <button
              type="button"
              onClick={onSearchEverywhere}
              className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700"
            >
              {t('bookmark.empty.searchEverywhere')}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (hasOtherFilters) {
    return (
      <div className="flex flex-col items-center gap-2.5 rounded-[18px] border border-gray-200 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-gray-800">
        <CheckCircleIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
        <p className="text-base font-bold text-gray-900 dark:text-white">
          {isFavoritesView
            ? t('bookmark.empty.favorites')
            : t('bookmark.empty.filterTitle')}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('bookmark.empty.filterHint')}
        </p>
        <button
          type="button"
          onClick={onClearFilters}
          className="mt-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {t('bookmark.clearFilters')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2.5 rounded-[18px] border-[1.5px] border-dashed border-gray-300 px-6 py-14 text-center dark:border-gray-600">
      <FolderOpenIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
      <p className="text-base font-bold text-gray-900 dark:text-white">
        {t('bookmark.empty.folderTitle')}
      </p>
      <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
        {t('bookmark.empty.folderHint')}
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
      >
        {t('bookmark.empty.createFirst')}
      </button>
    </div>
  )
}
